import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { Button } from "../../forms/Buttons/Button";
import { defaultSelectStyles, selectTheme } from "../../forms/Select/styles";
import { getQuestions, getSubtopics, getTopics } from "../../../services/questionService";
import { isCanceledError } from "../../../utils/examError";
import { fetchAllPages } from "../../../utils/fetchAllPages";
import { DIFFICULTY_LEVELS } from "../../Difficulty/Difficulty";
import {
  MAX_HOURS,
  MAX_ROWS,
  MAX_WEEK,
  countOf,
  gradeLabel,
  gradeOf,
  levelsText,
  quarterLabel,
  rowPlace,
  subtopicsOf,
  templateWeek,
  topicMarks,
  trimName,
} from "./matrixModel";
import { Spinner, linkButtonClass, secondaryButtonClass } from "./MatrixParts";

const selectStyles = {
  ...defaultSelectStyles,
  menuPortal: (base) => ({ ...base, zIndex: 60 }),
  option: (base, state) => ({
    ...defaultSelectStyles.option(base, state),
    ...(state.isDisabled ? { color: "#94a3b8", backgroundColor: "transparent", cursor: "not-allowed" } : {}),
  }),
};
const fieldClass = `h-10 rounded-lg border-slate-300 text-sm shadow-sm transition duration-200 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 max-md:h-11`;
const labelClass = "mb-1 block text-xs font-semibold text-slate-700";
// A radio or checkbox line: the whole line takes the tap, 44px tall on phones (its box centred there).
const choiceClass = "flex items-start gap-2 text-sm max-md:min-h-[2.75rem] max-md:items-center max-md:py-1";
const boxClass = "mt-0.5 h-4 w-4 shrink-0 border-slate-300 text-blue-600 focus:ring-blue-600 max-md:mt-0";
const emptyCounts = () => Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, 0]));

// A topic's place in the other rows of the draft: planned whole, or which of its subtopics have rows (subtopic uuid
// -> row).
function topicUsage(rows, topicUuid) {
  const usage = { whole: null, subs: new Map() };
  rows.forEach((row) => {
    if (!row.topic || row.topic.uuid !== topicUuid) return;
    const own = subtopicsOf(row);
    if (own.length) own.forEach((s) => usage.subs.set(s.uuid, row));
    else usage.whole = row;
  });
  return usage;
}

/**
 * One row of one quarter of one grade, in three steps: the week (and hours), a topic of that grade, then what the
 * row plans: some of the topic's subtopics (they share the row's A, B and C) or the whole topic. What the other rows
 * of the draft (`rows`) have is shown but cannot be picked again; next to each choice, how many approved questions of
 * it the subject's bank has. Topics of the same name (the catalog repeats some) show what tells them apart.
 * With `row` the form changes that draft row: it starts from its week, hours, topic and subtopics, and Apply replaces
 * them (its A, B and C stay). Without it the form adds a row, at 0 of every level, or for a week of the BTS
 * `template` the grade has no row in yet at the template's counts. `topicsCache` and `subtopicsCache` (topic uuid ->
 * its subtopics) keep what was read for the next form.
 * `onSubmit({topic, subtopics, week, hours, approved, planned})`: `subtopics` is [] for the whole topic; `approved`
 * the bank's approved counts {A, B, C} of the choice (null when not known); `planned` the template's counts or null.
 */
export default function AddRowForm({
  grade,
  quarter,
  folderUuid,
  subjectUuid,
  rows,
  row = null,
  template = null,
  topicsCache,
  subtopicsCache,
  full,
  onSubmit,
  onClose,
  className = "mt-3",
}) {
  const [t] = useTranslation();
  const ids = useId();
  const changing = !!row;
  const [week, setWeek] = useState(() => (row?.week ? String(row.week) : ""));
  const [hours, setHours] = useState(() => (row?.hours === null || row?.hours === undefined ? "" : String(row.hours)));
  const [topics, setTopics] = useState(() => (topicsCache.current?.subject === subjectUuid ? topicsCache.current.list : null));
  const [topicsFailed, setTopicsFailed] = useState(false);
  const [topicsAttempt, setTopicsAttempt] = useState(0);
  const [topicOption, setTopicOption] = useState(() =>
    row?.topic ? { value: row.topic.uuid, label: trimName(row.topic.name), hint: "", topic: row.topic } : null
  );
  const [subtopics, setSubtopics] = useState(null);
  const [subtopicsFailed, setSubtopicsFailed] = useState(false);
  const [subtopicsAttempt, setSubtopicsAttempt] = useState(0);
  const [bank, setBank] = useState(null);
  const [whole, setWhole] = useState(() => !!row?.topic && !subtopicsOf(row).length);
  const [checked, setChecked] = useState(() => new Set(subtopicsOf(row).map((s) => s.uuid)));
  // Every subtopic got ticked, so the form turned to the whole topic: said under it.
  const [allTicked, setAllTicked] = useState(false);
  const topicUuid = topicOption?.value || "";
  // The row being changed does not hold its own topic or subtopics against itself.
  const others = useMemo(() => (row ? rows.filter((r) => r.id !== row.id) : rows), [rows, row]);

  // The topics endpoint ignores `search`, so the subject's topics come once (kept for the next form) and are
  // filtered here by grade.
  useEffect(() => {
    if (!subjectUuid || topicsCache.current?.subject === subjectUuid) return undefined;
    const controller = new AbortController();
    setTopicsFailed(false);
    fetchAllPages((page) => getTopics({ params: { subject: subjectUuid, ...page }, signal: controller.signal }))
      .then((list) => {
        topicsCache.current = { subject: subjectUuid, list };
        setTopics(list);
      })
      .catch((err) => {
        if (!isCanceledError(err)) setTopicsFailed(true);
      });
    return () => controller.abort();
  }, [subjectUuid, topicsAttempt, topicsCache]);

  // A topic's subtopics, and the subject's approved questions of it counted by subtopic and difficulty.
  useEffect(() => {
    setSubtopics(null);
    setSubtopicsFailed(false);
    setBank(null);
    if (!topicUuid) return undefined;
    const controller = new AbortController();
    const cached = subtopicsCache.current.get(topicUuid);
    if (cached) setSubtopics(cached);
    else {
      fetchAllPages((page) => getSubtopics({ params: { topic: topicUuid, ...page }, signal: controller.signal }))
        .then((list) => {
          subtopicsCache.current.set(topicUuid, list);
          setSubtopics(list);
        })
        .catch((err) => {
          if (!isCanceledError(err)) setSubtopicsFailed(true);
        });
    }
    fetchAllPages((page) => getQuestions({ params: { folder: folderUuid, topic: topicUuid, ...page }, signal: controller.signal }))
      .then((questions) => {
        const counts = new Map([["whole", emptyCounts()]]);
        questions.forEach((question) => {
          const level = question.difficulty;
          if (question.status?.code !== "APPROVED" || !DIFFICULTY_LEVELS.includes(level)) return;
          counts.get("whole")[level] += 1;
          const sub = question.subtopic?.uuid;
          if (!sub) return;
          if (!counts.has(sub)) counts.set(sub, emptyCounts());
          counts.get(sub)[level] += 1;
        });
        setBank(counts);
      })
      .catch((err) => {
        // Without the counts the row still gets added; the table then says "not known yet".
        if (!isCanceledError(err)) setBank(new Map());
      });
    return () => controller.abort();
  }, [topicUuid, folderUuid, subtopicsAttempt, subtopicsCache]);

  const usage = useMemo(() => topicUsage(others, topicUuid), [others, topicUuid]);
  const gradeTopics = useMemo(
    () => (topics || []).filter((topic) => (Number.isInteger(topic.grade) ? topic.grade : 0) === grade),
    [topics, grade]
  );
  // A topic whose name another of the grade has: its code (and place).
  const marks = useMemo(() => topicMarks(t, gradeTopics), [t, gradeTopics]);
  // Each topic says how many subtopics it has. A topic is planned whole or by subtopics: one another row plans whole
  // cannot be picked again.
  const topicOptions = useMemo(() => {
    const used = new Set(others.filter((r) => r.topic && !subtopicsOf(r).length).map((r) => r.topic.uuid));
    return gradeTopics
      .map((topic) => {
        const count = topic.subtopics_count;
        const size = Number.isInteger(count) ? (count ? t("folders.matrix.subtopics_n", { count }) : t("folders.matrix.no_subtopics")) : "";
        return {
          value: topic.uuid,
          label: trimName(topic.name),
          hint: [marks.get(topic.uuid) || "", size].filter(Boolean).join(" · "),
          topic: { uuid: topic.uuid, name: trimName(topic.name), grade: topic.grade, code: topic.code },
          isDisabled: used.has(topic.uuid),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label) || a.hint.localeCompare(b.hint, undefined, { numeric: true }));
  }, [gradeTopics, marks, others, t]);
  // The option chosen, as the list words it (the row's own topic until the topics are read).
  const topicValue = topicOption ? topicOptions.find((option) => option.value === topicOption.value) || topicOption : null;

  const subtopicList = subtopics || [];
  const hasSubtopics = subtopicList.length > 0;
  const wholeBlocked = usage.subs.size > 0;
  const subtopicsBlocked = !!usage.whole;
  // A topic without subtopics, or one some of whose subtopics are planned, has only one way left.
  const planWhole = !wholeBlocked && (whole || (subtopics !== null && !hasSubtopics));
  const picked = planWhole ? [] : subtopicList.filter((s) => checked.has(s.uuid) && !usage.subs.has(s.uuid));
  const canSubmit = (changing || !full) && !!topicOption && (planWhole || picked.length > 0);

  // A week of the BTS template that this grade has no row in yet: the new row takes its counts.
  const weekNumber = quarter && week ? Number(week) : null;
  const templateCounts = useMemo(() => {
    const entry = !changing && weekNumber ? templateWeek(template, quarter, weekNumber) : null;
    const taken = entry && rows.some((r) => gradeOf(r) === grade && r.quarter === quarter && r.week === weekNumber);
    return entry && !taken ? entry.planned : null;
  }, [changing, template, quarter, weekNumber, rows, grade]);

  const pickTopic = (option) => {
    setTopicOption(option);
    setChecked(new Set());
    setWhole(false);
    setAllTicked(false);
  };
  const planBy = (wholeTopic) => {
    setWhole(wholeTopic);
    setAllTicked(false);
  };
  // Ticking the last subtopic plans the whole topic: that is what all of them are.
  const toggle = (uuid) => {
    const next = new Set(checked);
    if (next.has(uuid)) next.delete(uuid);
    else next.add(uuid);
    setChecked(next);
    if (!wholeBlocked && hasSubtopics && subtopicList.every((s) => next.has(s.uuid))) {
      setWhole(true);
      setAllTicked(true);
    }
  };

  // Next to each choice, the bank's approved questions of it. When the topic has none at all, that is said once (by
  // the legend) instead of on every line; while they are read, or when they could not be, nothing is said.
  const bankKnown = !!bank?.has("whole");
  const bankEmpty = bankKnown && countOf(bank.get("whole")) === 0;
  const bankText = (key) => {
    if (!bankKnown || bankEmpty) return null;
    const counts = bank.get(key) || emptyCounts();
    const total = countOf(counts);
    return total ? t("folders.matrix.form_bank", { count: total, levels: levelsText(counts) }) : t("folders.matrix.form_bank_none");
  };
  // The new row's bank: the whole topic's, or the sum of its subtopics' (unknown while not read, or when the
  // questions could not be read: that bank has no "whole").
  const approvedOf = () => {
    if (!bank?.has("whole")) return null;
    if (planWhole) return { ...bank.get("whole") };
    return picked.reduce((sum, s) => {
      const counts = bank.get(s.uuid) || emptyCounts();
      DIFFICULTY_LEVELS.forEach((level) => {
        sum[level] += counts[level];
      });
      return sum;
    }, emptyCounts());
  };

  const submit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      topic: topicValue.topic,
      subtopics: picked.map((s) => ({ uuid: s.uuid, name: trimName(s.name) })),
      week: weekNumber,
      hours: hours === "" ? "" : String(Math.min(MAX_HOURS, Number(hours))),
      approved: approvedOf(),
      planned: templateCounts,
    });
  };

  const footnote = changing
    ? t("folders.matrix.form_change_hint")
    : full
      ? t("folders.matrix.max_rows", { max: MAX_ROWS })
      : templateCounts
        ? t("folders.matrix.form_counts_template")
        : t("folders.matrix.form_counts_hint");
  const portal = typeof document !== "undefined" ? document.body : null;
  return (
    <form
      onSubmit={submit}
      aria-labelledby={`${ids}-title`}
      onKeyDown={(event) => {
        // react-select prevents every Escape it gets: only the one that closes its open menu keeps the form open.
        if (event.key === "Escape" && event.target.getAttribute?.("aria-expanded") !== "true") onClose();
      }}
      className={`${className} flex flex-col gap-4 rounded-xl border border-blue-200 bg-blue-50/40 p-4 text-left font-normal`}
    >
      {/* The form closes with its Cancel (or Esc): no second close button. */}
      <h4 id={`${ids}-title`} className="text-sm font-semibold text-slate-900">
        {t(changing ? "folders.matrix.form_change_title" : "folders.matrix.form_title", { grade: gradeLabel(t, grade), quarter: quarterLabel(t, quarter) })}
      </h4>

      <div className="flex flex-wrap items-start gap-3">
        {quarter && (
          <div>
            <label htmlFor={`${ids}-week`} className={labelClass}>
              {t("folders.matrix.form_week")}
            </label>
            <select
              id={`${ids}-week`}
              value={week}
              onChange={(e) => setWeek(e.target.value)}
              aria-describedby={templateCounts ? `${ids}-template` : undefined}
              className={`${fieldClass} w-36`}
            >
              <option value="">{t("folders.matrix.week_none")}</option>
              {Array.from({ length: MAX_WEEK }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {t("folders.matrix.week_n", { n })}
                </option>
              ))}
            </select>
            {templateCounts && (
              <p id={`${ids}-template`} className="mt-1 whitespace-nowrap text-xs font-medium tabular-nums text-blue-800">
                {t("folders.matrix.form_template_counts", { levels: levelsText(templateCounts) })}
              </p>
            )}
          </div>
        )}
        <div>
          <label htmlFor={`${ids}-hours`} className={labelClass}>
            {t("folders.matrix.form_hours")}
          </label>
          <input
            id={`${ids}-hours`}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={hours}
            placeholder="—"
            onChange={(e) => setHours(e.target.value.replace(/\D/g, "").slice(0, 2))}
            className={`${fieldClass} w-20 text-center`}
          />
        </div>
        <div className="min-w-0 flex-1 basis-72">
          <label htmlFor={`${ids}-topic`} className={labelClass}>
            {t("folders.matrix.form_topic", { grade: gradeLabel(t, grade) })}
          </label>
          <ReactSelect
            inputId={`${ids}-topic`}
            autoFocus
            styles={selectStyles}
            theme={selectTheme}
            menuPortalTarget={portal}
            menuPlacement="auto"
            options={topicOptions}
            value={topicValue}
            onChange={pickTopic}
            isLoading={!topics && !topicsFailed}
            isDisabled={topicsFailed}
            isClearable
            placeholder={t("folders.matrix.topic_placeholder")}
            noOptionsMessage={() => (topics ? t("folders.matrix.form_no_topics") : t("table.no_options"))}
            loadingMessage={() => t("table.loading")}
            // Spoken and searched with what tells it apart; shown with that muted.
            getOptionLabel={(option) => (option.hint ? `${option.label} (${option.hint})` : option.label)}
            formatOptionLabel={(option, { context }) => {
              const hint = option.hint ? <span className="text-xs text-slate-500"> · {option.hint}</span> : null;
              return context === "menu" ? (
                <span className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0">
                    {option.label}
                    {hint}
                  </span>
                  {option.isDisabled && <span className="shrink-0 text-xs text-slate-400">{t("folders.matrix.in_matrix")}</span>}
                </span>
              ) : (
                <>
                  {option.label}
                  {hint}
                </>
              );
            }}
          />
        </div>
      </div>

      {topicOption && (
        <fieldset className="min-w-0">
          <legend className={labelClass}>
            {t("folders.matrix.form_part")}
            {bankEmpty && <span className="ml-1.5 font-normal text-slate-500">· {t("folders.matrix.form_bank_none")}</span>}
          </legend>
          {subtopics === null ? (
            subtopicsFailed ? (
              <p role="alert" className="flex flex-wrap items-center gap-2 text-xs font-medium text-red-700">
                {t("folders.matrix.subtopics_failed")}
                <button type="button" onClick={() => setSubtopicsAttempt((n) => n + 1)} className={linkButtonClass}>
                  <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("folders.matrix.retry")}
                </button>
              </p>
            ) : (
              <p role="status" className="flex items-center gap-2 text-sm text-slate-500">
                <Spinner className="h-4 w-4" />
                {t("table.loading")}
              </p>
            )
          ) : !hasSubtopics ? (
            <p className="text-sm text-slate-600">
              {t("folders.matrix.form_no_subtopics")}
              {bankText("whole") && <span className="ml-1 text-xs text-slate-500">· {bankText("whole")}</span>}
            </p>
          ) : (
            <div className="flex flex-col gap-2 max-md:gap-0.5">
              <label className={`${choiceClass} ${subtopicsBlocked ? "text-slate-400" : "text-slate-800"}`}>
                <input
                  type="radio"
                  name={`${ids}-part`}
                  checked={!planWhole}
                  disabled={subtopicsBlocked}
                  onChange={() => planBy(false)}
                  className={boxClass}
                />
                <span id={`${ids}-by-subtopics`}>
                  {t("folders.matrix.form_by_subtopics")}
                  {subtopicsBlocked && <span className="block text-xs">{t("folders.matrix.form_whole_used")}</span>}
                </span>
              </label>
              {!planWhole && !subtopicsBlocked && (
                <div role="group" aria-labelledby={`${ids}-by-subtopics`} aria-describedby={`${ids}-share`} className="ml-6 border-l-2 border-blue-100 pl-3">
                  <p id={`${ids}-share`} className="mb-1.5 text-xs text-slate-500">
                    {t("folders.matrix.form_subtopics_hint")}
                  </p>
                  <ul className="flex flex-col gap-1.5 max-md:gap-0">
                    {subtopicList.map((s) => {
                      const used = usage.subs.get(s.uuid);
                      const note = used ? t("folders.matrix.form_in_matrix", { where: rowPlace(t, used) }) : bankText(s.uuid);
                      return (
                        <li key={s.uuid}>
                          <label className={`${choiceClass} ${used ? "cursor-not-allowed text-slate-400" : "cursor-pointer text-slate-800"}`}>
                            {/* A tick is what this row plans: another row's subtopic stays unticked (its note says where it is). */}
                            <input
                              type="checkbox"
                              checked={!used && checked.has(s.uuid)}
                              disabled={!!used}
                              onChange={() => toggle(s.uuid)}
                              className={`${boxClass} rounded`}
                            />
                            <span className="min-w-0">
                              <span className="break-words">{trimName(s.name)}</span>
                              {note && <span className="ml-1.5 text-xs text-slate-500">· {note}</span>}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <label className={`${choiceClass} ${wholeBlocked ? "text-slate-400" : "text-slate-800"}`}>
                <input
                  type="radio"
                  name={`${ids}-part`}
                  checked={planWhole}
                  disabled={wholeBlocked}
                  onChange={() => planBy(true)}
                  className={boxClass}
                />
                <span>
                  {t("folders.matrix.whole_topic")}
                  {wholeBlocked ? (
                    <span className="block text-xs">{t("folders.matrix.form_whole_blocked")}</span>
                  ) : (
                    bankText("whole") && <span className="ml-1.5 text-xs text-slate-500">· {bankText("whole")}</span>
                  )}
                  {allTicked && planWhole && (
                    <span role="status" className="block text-xs font-medium text-blue-800">
                      {t("folders.matrix.form_all_ticked")}
                    </span>
                  )}
                </span>
              </label>
            </div>
          )}
        </fieldset>
      )}

      {topicsFailed && (
        <p role="alert" className="flex flex-wrap items-center gap-2 text-xs font-medium text-red-700">
          {t("folders.matrix.topics_failed")}
          <button type="button" onClick={() => setTopicsAttempt((n) => n + 1)} className={linkButtonClass}>
            <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("folders.matrix.retry")}
          </button>
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 basis-60 text-xs text-slate-500">{footnote}</p>
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          {t("buttons.cancel")}
        </button>
        <Button
          type="submit"
          color="primary"
          text={changing ? t("folders.matrix.form_apply") : t("folders.matrix.form_add_row")}
          disabled={!canSubmit}
          className="max-md:h-11 max-md:flex-1"
        />
      </div>
    </form>
  );
}
